import type {AxisFeatureDefinition, AxisResolvedGeometry} from './reference-axis/ReferenceAxisSolver';
import type {ReferenceCreationContext} from './ReferenceCreationContext';


/**
 * 基准轴历史实体 ID。
 */
export const DATUM_AXIS_HISTORY_ENTITY_ID = 'datum-axis-manager';

/**
 * 基准轴历史三维向量。
 */
export type DatumAxisHistoryVector3 = readonly [number, number, number];

/**
 * 基准轴解析几何历史记录。
 */
export interface DatumAxisResolvedGeometryHistoryRecord {
  /** 轴线原点。 */
  origin: DatumAxisHistoryVector3;
  /** 轴线方向。 */
  direction: DatumAxisHistoryVector3;
  /** 显示线段起点。 */
  startWorld: DatumAxisHistoryVector3;
  /** 显示线段终点。 */
  endWorld: DatumAxisHistoryVector3;
  /** 有效性。 */
  validity: AxisResolvedGeometry['validity'];
}

/**
 * 单个正式基准轴历史记录。
 */
export interface DatumAxisHistoryRecord {
  /** 基准轴稳定业务 ID。 */
  axisId: number;
  /** 基准轴名称。 */
  name: string;
  /** 基准轴创建上下文。 */
  creationContext: ReferenceCreationContext;
  /** 轴线原点。 */
  originWorld: DatumAxisHistoryVector3;
  /** 轴线方向。 */
  direction: DatumAxisHistoryVector3;
  /** 显示线段起点。 */
  startWorld: DatumAxisHistoryVector3;
  /** 显示线段终点。 */
  endWorld: DatumAxisHistoryVector3;
  /** 显示颜色。 */
  color: number;
  /** 是否可见。 */
  visible: boolean;
  /** 不透明度。 */
  opacity: number;
  /** 用户手动创建时间戳（毫秒），用于设计树时间线排序。 */
  createdAt?: number;
}

/**
 * 用户手动创建基准轴的设计树时间线登记项。
 *
 * 设计说明：
 * 1. 当前没有默认内置基准轴，用户创建的零件态/装配体态轴都进入该时间线；
 * 2. 数组顺序与创建登记顺序一致，设计树投影可据此与特征/草图按时间合并排序；
 * 3. 该结构只保存 plain data，不持有任何 Three.js 或场景对象引用。
 */
export interface UserCreatedDatumAxisTimelineEntry {
  /** 基准轴稳定业务 ID。 */
  axisId: number;
  /** 创建时间戳（毫秒）。 */
  createdAt: number;
}

/**
 * 基准轴特征历史记录。
 */
export interface DatumAxisFeatureHistoryRecord {
  /** 特征 ID。 */
  featureId: string;
  /** 正式对象创建来源上下文。 */
  creationContext: ReferenceCreationContext;
  /** 基准轴可复算定义。 */
  definition: AxisFeatureDefinition;
  /** 已解析几何。 */
  resolvedGeometry?: DatumAxisResolvedGeometryHistoryRecord;
  /** 线/边/轴模式来源线段。 */
  sourceLinearSegment?: {
    /** 线段起点。 */
    start: DatumAxisHistoryVector3;
    /** 线段终点。 */
    end: DatumAxisHistoryVector3;
  };
  /** 是否隐藏。 */
  hidden: boolean;
  /** 正式基准轴 ID。 */
  datumAxisId?: number;
  /** 是否压缩。 */
  suppressed?: boolean;
  /** 错误信息。 */
  error?: string;
}

/**
 * 基准轴管理器历史快照。
 *
 * 设计说明：
 * 1. 快照只保存可序列化主状态，不保存 Three.js 对象；
 * 2. 用户创建的零件态和装配体态基准轴都通过该快照恢复；
 * 3. 控制器特征记录与正式对象快照共同恢复，保证撤销重做后仍可继续装配体联动重算。
 */
export interface DatumAxisHistorySnapshot {
  /** 管理器历史实体 ID。 */
  managerId: string;
  /** 用户创建的正式基准轴记录。 */
  axes: DatumAxisHistoryRecord[];
  /** 用户创建的基准轴特征记录。 */
  features: DatumAxisFeatureHistoryRecord[];
}

